import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Divider,
    Paper,
    Stack
} from '@mui/material';
import { IconX } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { addReadNoticeIds, setDontShowToday, setLastSeenNoticeCombination } from 'utils/utilsNoticeStorage';
import SimpleEditor from 'components/ui-component/editors/SimpleEditor';

/**
 * 공지사항 팝업 모달 컴포넌트
 * @param {boolean} open - 모달 열림 상태
 * @param {function} onClose - 모달 닫기 함수
 * @param {Array} notices - 공지사항 배열
 */
const NoticePopupModal = ({ open, onClose, notices = [] }) => {
    const navigate = useNavigate();

    if (!notices || notices.length === 0) {
        return null;
    }

    // 상세보기로 이동
    const handleViewDetail = (noticeNo) => {
        if (noticeNo) {
            // 해당 공지사항 읽음 처리
            addReadNoticeIds([noticeNo]);
            // 모달 닫기
            onClose();
            // 상세보기 페이지로 이동
            navigate(`/board/notice/detail/${noticeNo}`);
        }
    };

    // 닫기 (모든 공지사항 읽음 처리)
    const handleClose = () => {
        const noticeIds = notices
            .filter(notice => notice && (notice.no || notice.NO))
            .map(notice => notice.no || notice.NO);
        if (noticeIds.length > 0) {
            addReadNoticeIds(noticeIds);
            setLastSeenNoticeCombination(noticeIds);
        }
        onClose();
    };

    // 오늘 하루 안보기
    const handleDontShowToday = () => {
        setDontShowToday();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={() => {}} // 외부 클릭으로 닫히지 않도록 빈 함수
            disableEscapeKeyDown={true} // ESC 키로 닫히지 않도록
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                pb: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" component="div">
                        공지사항 {notices.length > 1 && `(${notices.length}건)`}
                    </Typography>
                </Box>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={handleClose}
                    aria-label="닫기"
                    size="small"
                >
                    <IconX size={20} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pb: 2, px: 0 }}>
                <Box sx={{ 
                    maxHeight: '60vh',
                    overflowY: 'auto',
                    px: 3
                }}>
                    <Stack spacing={3}>
                        {notices.map((notice, index) => (
                            <Paper key={notice.no || notice.NO || index} sx={{ p: 2 }}>
                                <Stack spacing={2}>
                                    {/* 제목 */}
                                    <Box>
                                        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {notice.title || '제목 없음'}
                                        </Typography>
                                        {notice.regDate && (
                                            <Typography variant="body2" color="text.secondary">
                                                등록일: {notice.regDate}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Divider />

                                    {/* 내용 */}
                                    <Box sx={{ 
                                        minHeight: '150px',
                                        '& .tiptap': {
                                            padding: 0,
                                            border: 'none',
                                            backgroundColor: 'transparent'
                                        }
                                    }}>
                                        <SimpleEditor 
                                            value={notice.content || ''} 
                                            onChange={() => {}} 
                                            editable={false}
                                        />
                                    </Box>

                                    {/* 상세보기 버튼 */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => handleViewDetail(notice.no || notice.NO)}
                                        >
                                            상세보기
                                        </Button>
                                    </Box>
                                </Stack>
                                {index < notices.length - 1 && (
                                    <Divider sx={{ mt: 3 }} />
                                )}
                            </Paper>
                        ))}
                    </Stack>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Box />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={handleDontShowToday}
                        >
                            오늘 하루 안보기
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleClose}
                        >
                            확인
                        </Button>
                    </Box>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default NoticePopupModal;
